import { create } from 'zustand';
import { MessageCitation } from '@/types/chat';
import { normalizeUrl } from '@/lib/urlNormalize';
import { debugLog } from '@/lib/debug';

interface CitationState {
  // Selected citations to display in canvas (1-to-1 with a specific message)
  selectedMessageCitations: MessageCitation[] | null;
  // Track the last message ID that triggered auto-open to avoid re-triggering
  lastAutoOpenedMessageId: string | null;
  // Last citation IDs set (for efficient change detection during streaming)
  lastCitationIds: Set<string> | null;
  
  // Canvas accordion state (merged from canvasAccordionStore)
  activeUrl: string | null; // URL being hovered (from LinkPill) - takes precedence
  expandedUrl: string | null; // URL that is expanded via hover on CanvasItem
  selectedUrl: string | null; // URL that was clicked/selected - persists until toggled
  
  // Actions
  closeCanvas: () => void;
  showCitations: (citations: MessageCitation[]) => void;
  clearCitations: () => void;
  autoOpenForMessage: (messageId: string, citations: MessageCitation[]) => void;
  
  // Accordion actions
  setActiveUrl: (url: string | null) => void;
  setExpandedUrl: (url: string | null) => void;
  setSelectedUrl: (url: string | null) => void;
}

export const useCitationStore = create<CitationState>((set, get) => ({
  selectedMessageCitations: null,
  lastAutoOpenedMessageId: null,
  lastCitationIds: null,
  
  // Accordion state
  activeUrl: null,
  expandedUrl: null,
  selectedUrl: null,

  closeCanvas: () => 
    set({ 
      selectedMessageCitations: null, 
      lastAutoOpenedMessageId: null,
      lastCitationIds: null,
      selectedUrl: null, // Clear selected URL when closing
    }),

  showCitations: (citations) => {
    if (!citations || citations.length === 0) {
      set({ 
        selectedMessageCitations: null,
        lastCitationIds: null,
      });
      return;
    }
    
    // Validate and filter citations
    const validCitations = citations.filter(c => {
      if (!c || typeof c !== 'object') return false;
      if (!c.url || typeof c.url !== 'string') return false;
      // Basic URL validation
      try {
        new URL(c.url);
        return true;
      } catch {
        return false;
      }
    });
    
    if (validCitations.length === 0) {
      set({ 
        selectedMessageCitations: null,
        lastCitationIds: null,
      });
      return;
    }
    
    // Create citation ID set for efficient comparison
    const citationIds = new Set(validCitations.map(c => c.id).filter(Boolean));
    
    set({ 
      selectedMessageCitations: validCitations,
      lastCitationIds: citationIds,
    });
  },

  clearCitations: () => 
    set({ 
      selectedMessageCitations: null, 
      lastAutoOpenedMessageId: null,
      lastCitationIds: null,
      selectedUrl: null,
    }),

  autoOpenForMessage: (messageId, citations) => {
    debugLog.canvas('autoOpenForMessage called', {
      messageId,
      citationsCount: citations?.length || 0,
      citations: citations?.map(c => ({ id: c.id, url: c.url })) || [],
    });
    
    if (!messageId || typeof messageId !== 'string') {
      debugLog.warn('autoOpenForMessage: Invalid messageId', { messageId });
      return;
    }
    
    if (!citations || !Array.isArray(citations) || citations.length === 0) {
      debugLog.canvas('autoOpenForMessage: No citations provided, skipping');
      return;
    }
    
    // Validate and filter citations
    const validCitations = citations.filter(c => {
      if (!c || typeof c !== 'object') return false;
      if (!c.url || typeof c.url !== 'string') return false;
      try {
        new URL(c.url);
        return true;
      } catch {
        return false;
      }
    });
    
    if (validCitations.length === 0) {
      debugLog.canvas('autoOpenForMessage: No valid citations after filtering');
      return;
    }
    
    const { lastAutoOpenedMessageId, lastCitationIds } = get();
    
    // Create citation ID set for efficient comparison (order-independent)
    const currentCitationIds = new Set(validCitations.map(c => c.id).filter(Boolean));
    
    // Check if this is a new message
    const isNewMessage = messageId !== lastAutoOpenedMessageId;
    
    // Check if citations changed using Set comparison (order-independent, more efficient)
    const citationsChanged = lastCitationIds === null || 
      lastCitationIds.size !== currentCitationIds.size ||
      !Array.from(currentCitationIds).every(id => lastCitationIds.has(id));
    
    debugLog.canvas('autoOpenForMessage decision', {
      messageId,
      lastAutoOpenedMessageId,
      isNewMessage,
      citationsChanged,
      lastCitationIdsSize: lastCitationIds?.size || 0,
      currentCitationIdsSize: currentCitationIds.size,
      currentCitationIds: Array.from(currentCitationIds),
      willOpen: isNewMessage || citationsChanged,
    });
    
    // Auto-open if new message or citations changed
    if (isNewMessage || citationsChanged) {
      debugLog.canvas('✅ Opening canvas', {
        messageId,
        citationsCount: validCitations.length,
        reason: isNewMessage ? 'new message' : 'citations changed',
      });
      set({ 
        selectedMessageCitations: validCitations,
        lastAutoOpenedMessageId: messageId,
        lastCitationIds: currentCitationIds,
      });
    } else {
      debugLog.canvas('⏭️ Skipping auto-open (same message, no citation changes)');
    }
  },
  
  // Accordion actions with URL validation
  setActiveUrl: (url) => {
    if (!url) {
      set({ activeUrl: null });
      return;
    }
    try {
      // Validate URL before normalizing
      new URL(url);
      set({ activeUrl: normalizeUrl(url) });
    } catch {
      // Invalid URL, ignore
      set({ activeUrl: null });
    }
  },
  setExpandedUrl: (url) => {
    if (!url) {
      set({ expandedUrl: null });
      return;
    }
    try {
      new URL(url);
      set({ expandedUrl: normalizeUrl(url) });
    } catch {
      set({ expandedUrl: null });
    }
  },
  setSelectedUrl: (url) => {
    if (!url) {
      set({ selectedUrl: null });
      return;
    }
    try {
      new URL(url);
      set({ selectedUrl: normalizeUrl(url) });
    } catch {
      set({ selectedUrl: null });
    }
  },
}));

// Selector for canvas open state (computed from citations)
export const useCanvasOpen = () => {
  return useCitationStore((state) => 
    state.selectedMessageCitations !== null && state.selectedMessageCitations.length > 0
  );
};

